/**
 * What the server accepts as an attachment (ADR-0021): one definition used
 * by the API to refuse uploads and by the device to stop the reporter from
 * picking a file that would be refused later, offline, when nobody is
 * looking (a refused upload would otherwise block the survey's sync queue).
 */

export const ATTACHMENT_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] as const;
export const ATTACHMENT_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'] as const;
export const ATTACHMENT_DOCUMENT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

export type UploadKind = 'PHOTO' | 'FILE' | 'EVIDENCE' | 'ACCEPTANCE' | 'SIGNATURE';

export const ATTACHMENT_TYPES: Record<UploadKind, readonly string[]> = {
  PHOTO: ATTACHMENT_IMAGE_TYPES,
  SIGNATURE: ['image/png'],
  FILE: [...ATTACHMENT_IMAGE_TYPES, ...ATTACHMENT_VIDEO_TYPES, ...ATTACHMENT_DOCUMENT_TYPES],
  EVIDENCE: [...ATTACHMENT_IMAGE_TYPES, ...ATTACHMENT_VIDEO_TYPES, ...ATTACHMENT_DOCUMENT_TYPES],
  ACCEPTANCE: [...ATTACHMENT_IMAGE_TYPES, ...ATTACHMENT_VIDEO_TYPES, ...ATTACHMENT_DOCUMENT_TYPES],
};

/** Largest file the API accepts (the multipart limit uses the same number). */
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

/** File-name extensions for the document types, so pickers that ignore MIME types still filter. */
const EXTENSIONS: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'image/jpeg': '.jpg,.jpeg',
  'image/png': '.png',
  'image/webp': '.webp',
  'image/heic': '.heic',
  'video/mp4': '.mp4',
  'video/webm': '.webm',
  'video/quicktime': '.mov',
};

/** Value for an `<input type="file" accept>` that offers only what the server accepts. */
export function acceptAttribute(kind: UploadKind): string {
  const types = ATTACHMENT_TYPES[kind];
  return [...types, ...types.map((t) => EXTENSIONS[t]).filter(Boolean)].join(',');
}

export type AttachmentProblem = 'UNSUPPORTED_TYPE' | 'TOO_LARGE';

/** Why the server would refuse this file, or null when it is acceptable. */
export function attachmentProblem(kind: UploadKind, contentType: string, size: number): AttachmentProblem | null {
  if (!ATTACHMENT_TYPES[kind].includes(contentType)) return 'UNSUPPORTED_TYPE';
  if (size > MAX_ATTACHMENT_BYTES) return 'TOO_LARGE';
  return null;
}
